import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import GlobalStyles from './theme/GlobalStyles';
import ProtectedRoute from './components/ProtectedRoute';
import SurveyGate from './components/SurveyGate';
import PrintPreviewHost from './components/PrintPreview';
import PrintBrandHost from './components/PrintBrandHost';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SuperAdmin from './pages/SuperAdmin';
import Dashboard from './pages/Dashboard';

// Settings
import MyCompany from './pages/settings/MyCompany';
import UsersRoles from './pages/settings/UsersRoles';
import FinancialYear from './pages/settings/FinancialYear';
import DocumentSettings from './pages/settings/DocumentSettings';
import PrintSettings from './pages/settings/PrintSettings';
import InventoryMovementSettings from './pages/settings/InventoryMovementSettings';
import BranchSetup from './pages/settings/BranchSetup';
import CounterSetup from './pages/settings/CounterSetup';
import Backup from './pages/settings/Backup';

// Master Data
import ChartOfAccounts from './pages/master/ChartOfAccounts';
import Customers from './pages/master/Customers';
import Vendors from './pages/master/Vendors';
import Banks from './pages/master/Banks';
import ChartOfInventory from './pages/master/ChartOfInventory';
import BulkItemUpdate from './pages/master/BulkItemUpdate';
import InventoryLocations from './pages/master/InventoryLocations';
import BarcodeLabels from './pages/master/BarcodeLabels';
import BankDiscounts from './pages/master/BankDiscounts';
import Employees from './pages/master/Employees';
import Regions from './pages/master/Regions';
import Jobs from './pages/master/Jobs';
import OpeningBalances from './pages/master/OpeningBalances';
import ImportChargeTemplates from './pages/master/ImportChargeTemplates';
import Units from './pages/master/Units';

// Purchases
import PurchaseOrder from './pages/purchases/PurchaseOrder';
import GRN from './pages/purchases/GRN';
import PurchaseInvoice from './pages/purchases/PurchaseInvoice';
import PurchaseReturn from './pages/purchases/PurchaseReturn';
import PurchaseNonTaxInvoice from './pages/purchases/PurchaseNonTaxInvoice';
import PurchaseNonTaxReturn from './pages/purchases/PurchaseNonTaxReturn';
import ImportPurchaseInvoice from './pages/purchases/ImportPurchaseInvoice';

// Sales
import Quotation from './pages/sales/Quotation';
import SaleOrder from './pages/sales/SaleOrder';
import DeliveryChallan from './pages/sales/DeliveryChallan';
import SaleInvoice from './pages/sales/SaleInvoice';
import SaleReturn from './pages/sales/SaleReturn';
import SaleNonTaxInvoice from './pages/sales/SaleNonTaxInvoice';
import SaleNonTaxReturn from './pages/sales/SaleNonTaxReturn';

// POS
import POSScreen from './pages/pos/POSScreen';

// Store
import PurchaseRequisition from './pages/store/PurchaseRequisition';
import InwardGatePass from './pages/store/InwardGatePass';
import OutwardGatePass from './pages/store/OutwardGatePass';
import BillOfMaterials from './pages/store/BillOfMaterials';
import ProductionOrder from './pages/store/ProductionOrder';
import AddStock from './pages/store/AddStock';
import ReduceStock from './pages/store/ReduceStock';
import StockAudit from './pages/store/StockAudit';
import StockAuditList from './pages/reports/StockAuditList';
import CountDifferenceReport from './pages/reports/CountDifferenceReport';
import StockLossSummary from './pages/reports/StockLossSummary';
import MostMissingItems from './pages/reports/MostMissingItems';
import UserActivityLog from './pages/reports/UserActivityLog';
import StockTransfer from './pages/store/StockTransfer';
import BranchTransfer from './pages/store/BranchTransfer';
import SendInventory from './pages/store/SendInventory';
import ReceiveInventory from './pages/store/ReceiveInventory';
import MaterialIssue from './pages/store/MaterialIssue';
import ProductionReceipt from './pages/store/ProductionReceipt';
import AIStudio from './pages/AIStudio';

// Receipts
import CashReceipts from './pages/receipts/CashReceipts';
import BankReceipts from './pages/receipts/BankReceipts';

// Payments
import CashPayment from './pages/payments/CashPayment';
import BankPayment from './pages/payments/BankPayment';

// Journal
import GeneralJournal from './pages/journal/GeneralJournal';
import CashBook from './pages/journal/CashBook';

// Salary
import AssetRegister from './pages/assets/AssetRegister';
import AssetCategories from './pages/assets/AssetCategories';
import DepreciationRun from './pages/assets/DepreciationRun';
import AssetDisposal from './pages/assets/AssetDisposal';
import AssetAppreciation from './pages/assets/AssetAppreciation';
import AssetRegisterReport from './pages/assets/AssetRegisterReport';
import DepreciationSchedule from './pages/assets/DepreciationSchedule';
import FBRDepreciationStatement from './pages/assets/FBRDepreciationStatement';
import AssetDisposalsReport from './pages/assets/AssetDisposalsReport';
import AssetMovement from './pages/assets/AssetMovement';
import SalaryStaff from './pages/salary/SalaryStaff';
import SalaryAdvance from './pages/salary/SalaryAdvance';
import SalaryLoan from './pages/salary/SalaryLoan';
import LoanRecovery from './pages/salary/LoanRecovery';
import PayrollRun from './pages/salary/PayrollRun';
import FinalSettlement from './pages/salary/FinalSettlement';
import Attendance from './pages/salary/Attendance';
import AttendanceSettings from './pages/salary/AttendanceSettings';
import SalaryRegister from './pages/reports/SalaryRegister';
import StaffLoanAdvance from './pages/reports/StaffLoanAdvance';
import StatutoryContributions from './pages/reports/StatutoryContributions';

// WhatsApp
import WhatsAppConnect from './pages/whatsapp/WhatsAppConnect';
import WhatsAppTemplates from './pages/whatsapp/WhatsAppTemplates';
import WhatsAppLogs from './pages/whatsapp/WhatsAppLogs';
import WhatsAppSettings from './pages/whatsapp/WhatsAppSettings';

// Reports - Main
import JournalReport from './pages/reports/JournalReport';
import Vouchers from './pages/reports/Vouchers';
import SerialTracking from './pages/reports/SerialTracking';
import POTracking from './pages/reports/POTracking';
import SOTracking from './pages/reports/SOTracking';
import GRNTracking from './pages/reports/GRNTracking';
import DCTracking from './pages/reports/DCTracking';
import DraftReport from './pages/reports/DraftReport';

// Reports - Purchase
import PurchaseSummary from './pages/reports/PurchaseSummary';
import LandedCostAnalysis from './pages/reports/LandedCostAnalysis';
import PurchaseRegister from './pages/reports/PurchaseRegister';
import PartyPurchaseSummary from './pages/reports/PartyPurchaseSummary';
import VendorPayments from './pages/reports/VendorPayments';
import PurchaseActivity from './pages/reports/PurchaseActivity';
import ItemPurchaseAnalysis from './pages/reports/ItemPurchaseAnalysis';
import APAging from './pages/reports/APAging';
import PurchaseCycleReport from './pages/reports/PurchaseCycleReport';

// Reports - Sale
import SaleSummary from './pages/reports/SaleSummary';
import SaleCycleReport from './pages/reports/SaleCycleReport';
import SaleRegister from './pages/reports/SaleRegister';
import PartySaleSummary from './pages/reports/PartySaleSummary';
import CollectionReport from './pages/reports/CollectionReport';
import SaleActivity from './pages/reports/SaleActivity';
import ItemSaleAnalysis from './pages/reports/ItemSaleAnalysis';
import ARAging from './pages/reports/ARAging';
import SalespersonPerformance from './pages/reports/SalespersonPerformance';
import SalesmanIncentive from './pages/reports/SalesmanIncentive';
import POSSalesReport from './pages/reports/POSSalesReport';
import BankDiscountReport from './pages/reports/BankDiscountReport';
import POSShiftReport from './pages/reports/POSShiftReport';
import POSBranchCashReport from './pages/reports/POSBranchCashReport';
import ItemProfitLoss from './pages/reports/ItemProfitLoss';
import InvoiceProfitLoss from './pages/reports/InvoiceProfitLoss';

// Reports - Inventory
import InventoryLedger from './pages/reports/InventoryLedger';
import InventoryBalances from './pages/reports/InventoryBalances';
import LowStockAlert from './pages/reports/LowStockAlert';

// Reports - Party Balances
import CustomerBalances from './pages/reports/CustomerBalances';
import VendorBalances from './pages/reports/VendorBalances';
import CashBankBalances from './pages/reports/CashBankBalances';

// Reports - Financial
import Ledger from './pages/reports/Ledger';
import TrialBalance from './pages/reports/TrialBalance';
import ProfitLoss from './pages/reports/ProfitLoss';
import BalanceSheet from './pages/reports/BalanceSheet';
import CashFlow from './pages/reports/CashFlow';
import TaxReports from './pages/reports/TaxReports';

// Reports - Dashboard Intelligence
import FinancialHealthReport from './pages/reports/FinancialHealthReport';
import SalesIntelligenceReport from './pages/reports/SalesIntelligenceReport';
import InventoryIntelligenceReport from './pages/reports/InventoryIntelligenceReport';
import StockTransferReport from './pages/reports/StockTransferReport';
import BranchTransferReport from './pages/reports/BranchTransferReport';
import TransferReport from './pages/reports/TransferReport';
import CashFlowManagementReport from './pages/reports/CashFlowManagementReport';
import OperationalMetricsReport from './pages/reports/OperationalMetricsReport';

// Reports - Jobs & Production
import JobCostSheet from './pages/reports/JobCostSheet';
import JobProfitLoss from './pages/reports/JobProfitLoss';
import ProductionSummary from './pages/reports/ProductionSummary';
import ProductionVariance from './pages/reports/ProductionVariance';
import MaterialIssueReport from './pages/reports/MaterialIssueReport';
import ProductionReceiptReport from './pages/reports/ProductionReceiptReport';

import Help from './pages/Help';

// Onboarding wizard — self-gates: only renders for a new company's owner.
import OnboardingWizard from './pages/onboarding/OnboardingWizard';

// PWA UI helpers — both self-gate to no-op when not applicable
import UpdateBanner from './components/UpdateBanner';
import InstallPrompt from './components/InstallPrompt';

function App() {
  return (
    <Router>
      <AuthProvider>
        <GlobalStyles />
        <UpdateBanner />
        <InstallPrompt />
        <SurveyGate />
        <PrintPreviewHost />
        <PrintBrandHost />
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/super-admin" element={<SuperAdmin />} />

          {/* Dashboard */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          {/* Settings Routes */}
          <Route path="/settings/company" element={<ProtectedRoute><MyCompany /></ProtectedRoute>} />
          <Route path="/settings/users" element={<ProtectedRoute><UsersRoles /></ProtectedRoute>} />
          <Route path="/settings/financial-year" element={<ProtectedRoute><FinancialYear /></ProtectedRoute>} />
          <Route path="/settings/documents" element={<ProtectedRoute><DocumentSettings /></ProtectedRoute>} />
          <Route path="/settings/print-formats" element={<ProtectedRoute><PrintSettings /></ProtectedRoute>} />
          <Route path="/settings/inventory-movement" element={<ProtectedRoute><InventoryMovementSettings /></ProtectedRoute>} />
          <Route path="/settings/branches" element={<ProtectedRoute><BranchSetup /></ProtectedRoute>} />
          <Route path="/settings/pos-counters" element={<ProtectedRoute><CounterSetup /></ProtectedRoute>} />
          <Route path="/settings/backup" element={<ProtectedRoute><Backup /></ProtectedRoute>} />

          {/* Master Data Routes */}
          <Route path="/master/chart-of-accounts" element={<ProtectedRoute><ChartOfAccounts /></ProtectedRoute>} />
          <Route path="/master/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
          <Route path="/master/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
          <Route path="/master/banks" element={<ProtectedRoute><Banks /></ProtectedRoute>} />
          <Route path="/master/chart-of-inventory" element={<ProtectedRoute><ChartOfInventory /></ProtectedRoute>} />
          <Route path="/master/bulk-item-update" element={<ProtectedRoute><BulkItemUpdate /></ProtectedRoute>} />
          <Route path="/master/inventory-locations" element={<ProtectedRoute><InventoryLocations /></ProtectedRoute>} />
          <Route path="/master/employees" element={<ProtectedRoute><Employees /></ProtectedRoute>} />
          <Route path="/master/regions" element={<ProtectedRoute><Regions /></ProtectedRoute>} />
          <Route path="/master/jobs" element={<ProtectedRoute><Jobs /></ProtectedRoute>} />
          <Route path="/master/opening-balances" element={<ProtectedRoute><OpeningBalances /></ProtectedRoute>} />
          <Route path="/master/import-charge-templates" element={<ProtectedRoute><ImportChargeTemplates /></ProtectedRoute>} />
          <Route path="/master/units" element={<ProtectedRoute><Units /></ProtectedRoute>} />
          <Route path="/inventory/labels" element={<ProtectedRoute><BarcodeLabels /></ProtectedRoute>} />
          <Route path="/master/bank-discounts" element={<ProtectedRoute><BankDiscounts /></ProtectedRoute>} />

          {/* Purchase Routes */}
          <Route path="/purchases/purchase-order" element={<ProtectedRoute><PurchaseOrder /></ProtectedRoute>} />
          <Route path="/purchases/grn" element={<ProtectedRoute><GRN /></ProtectedRoute>} />
          <Route path="/purchases/purchase-invoice" element={<ProtectedRoute><PurchaseInvoice /></ProtectedRoute>} />
          <Route path="/purchases/purchase-return" element={<ProtectedRoute><PurchaseReturn /></ProtectedRoute>} />
          <Route path="/purchases/non-tax-invoice" element={<ProtectedRoute><PurchaseNonTaxInvoice /></ProtectedRoute>} />
          <Route path="/purchases/non-tax-purchase-return" element={<ProtectedRoute><PurchaseNonTaxReturn /></ProtectedRoute>} />
          <Route path="/purchases/import-invoice" element={<ProtectedRoute><ImportPurchaseInvoice /></ProtectedRoute>} />

          {/* Sales Routes */}
          <Route path="/sales/quotation" element={<ProtectedRoute><Quotation /></ProtectedRoute>} />
          <Route path="/sales/sale-order" element={<ProtectedRoute><SaleOrder /></ProtectedRoute>} />
          <Route path="/sales/delivery-challan" element={<ProtectedRoute><DeliveryChallan /></ProtectedRoute>} />
          <Route path="/sales/sale-invoice" element={<ProtectedRoute><SaleInvoice /></ProtectedRoute>} />
          <Route path="/sales/sale-return" element={<ProtectedRoute><SaleReturn /></ProtectedRoute>} />
          <Route path="/sales/non-tax-invoice" element={<ProtectedRoute><SaleNonTaxInvoice /></ProtectedRoute>} />
          <Route path="/sales/non-tax-sale-return" element={<ProtectedRoute><SaleNonTaxReturn /></ProtectedRoute>} />

          {/* POS Route */}
          <Route path="/pos" element={<ProtectedRoute><POSScreen /></ProtectedRoute>} />

          {/* Store Routes */}
          <Route path="/store/purchase-requisition" element={<ProtectedRoute><PurchaseRequisition /></ProtectedRoute>} />
          <Route path="/store/inward-gate-pass" element={<ProtectedRoute><InwardGatePass /></ProtectedRoute>} />
          <Route path="/store/outward-gate-pass" element={<ProtectedRoute><OutwardGatePass /></ProtectedRoute>} />
          <Route path="/store/bill-of-materials" element={<ProtectedRoute><BillOfMaterials /></ProtectedRoute>} />
          <Route path="/store/production-order" element={<ProtectedRoute><ProductionOrder /></ProtectedRoute>} />
          <Route path="/store/add-stock" element={<ProtectedRoute><AddStock /></ProtectedRoute>} />
          <Route path="/store/reduce-stock" element={<ProtectedRoute><ReduceStock /></ProtectedRoute>} />
          <Route path="/store/stock-audit" element={<ProtectedRoute><StockAudit /></ProtectedRoute>} />
          <Route path="/store/stock-transfer" element={<ProtectedRoute><StockTransfer /></ProtectedRoute>} />
          <Route path="/store/branch-transfer" element={<ProtectedRoute><BranchTransfer /></ProtectedRoute>} />
          <Route path="/store/send-inventory" element={<ProtectedRoute><SendInventory /></ProtectedRoute>} />
          <Route path="/store/receive-inventory" element={<ProtectedRoute><ReceiveInventory /></ProtectedRoute>} />
          <Route path="/store/material-issue" element={<ProtectedRoute><MaterialIssue /></ProtectedRoute>} />
          <Route path="/store/production-receipt" element={<ProtectedRoute><ProductionReceipt /></ProtectedRoute>} />
          <Route path="/ai-import" element={<ProtectedRoute><AIStudio /></ProtectedRoute>} />

          {/* Receipt Routes */}
          <Route path="/receipts/cash-receipt" element={<ProtectedRoute><CashReceipts /></ProtectedRoute>} />
          <Route path="/receipts/bank-receipt" element={<ProtectedRoute><BankReceipts /></ProtectedRoute>} />

          {/* Payment Routes */}
          <Route path="/payments/cash-payment" element={<ProtectedRoute><CashPayment /></ProtectedRoute>} />
          <Route path="/payments/bank-payment" element={<ProtectedRoute><BankPayment /></ProtectedRoute>} />

          {/* Journal Routes */}
          <Route path="/journal/general" element={<ProtectedRoute><GeneralJournal /></ProtectedRoute>} />
          <Route path="/journal/cash-book" element={<ProtectedRoute><CashBook /></ProtectedRoute>} />

          {/* Fixed Assets Routes */}
          <Route path="/assets/register" element={<ProtectedRoute><AssetRegister /></ProtectedRoute>} />
          <Route path="/assets/categories" element={<ProtectedRoute><AssetCategories /></ProtectedRoute>} />
          <Route path="/assets/depreciation" element={<ProtectedRoute><DepreciationRun /></ProtectedRoute>} />
          <Route path="/assets/disposal" element={<ProtectedRoute><AssetDisposal /></ProtectedRoute>} />
          <Route path="/assets/appreciation" element={<ProtectedRoute><AssetAppreciation /></ProtectedRoute>} />
          <Route path="/reports/asset-register" element={<ProtectedRoute><AssetRegisterReport /></ProtectedRoute>} />
          <Route path="/reports/depreciation-schedule" element={<ProtectedRoute><DepreciationSchedule /></ProtectedRoute>} />
          <Route path="/reports/fbr-depreciation" element={<ProtectedRoute><FBRDepreciationStatement /></ProtectedRoute>} />
          <Route path="/reports/asset-disposals" element={<ProtectedRoute><AssetDisposalsReport /></ProtectedRoute>} />
          <Route path="/reports/asset-movement" element={<ProtectedRoute><AssetMovement /></ProtectedRoute>} />

          {/* Salary Routes */}
          <Route path="/salary/staff" element={<ProtectedRoute><SalaryStaff /></ProtectedRoute>} />
          <Route path="/salary/advance" element={<ProtectedRoute><SalaryAdvance /></ProtectedRoute>} />
          <Route path="/salary/loan" element={<ProtectedRoute><SalaryLoan /></ProtectedRoute>} />
          <Route path="/salary/loan-recovery" element={<ProtectedRoute><LoanRecovery /></ProtectedRoute>} />
          <Route path="/salary/payroll-run" element={<ProtectedRoute><PayrollRun /></ProtectedRoute>} />
          <Route path="/salary/final-settlement" element={<ProtectedRoute><FinalSettlement /></ProtectedRoute>} />
          <Route path="/salary/attendance" element={<ProtectedRoute><Attendance /></ProtectedRoute>} />
          <Route path="/salary/attendance-settings" element={<ProtectedRoute><AttendanceSettings /></ProtectedRoute>} />
          <Route path="/reports/salary-register" element={<ProtectedRoute><SalaryRegister /></ProtectedRoute>} />
          <Route path="/reports/staff-loan-advance" element={<ProtectedRoute><StaffLoanAdvance /></ProtectedRoute>} />
          <Route path="/reports/statutory-contributions" element={<ProtectedRoute><StatutoryContributions /></ProtectedRoute>} />

          {/* WhatsApp Routes */}
          <Route path="/whatsapp/connect" element={<ProtectedRoute><WhatsAppConnect /></ProtectedRoute>} />
          <Route path="/whatsapp/templates" element={<ProtectedRoute><WhatsAppTemplates /></ProtectedRoute>} />
          <Route path="/whatsapp/logs" element={<ProtectedRoute><WhatsAppLogs /></ProtectedRoute>} />
          <Route path="/whatsapp/settings" element={<ProtectedRoute><WhatsAppSettings /></ProtectedRoute>} />

          {/* Report Routes - Main */}
          <Route path="/reports/journal" element={<ProtectedRoute><JournalReport /></ProtectedRoute>} />
          <Route path="/reports/vouchers" element={<ProtectedRoute><Vouchers /></ProtectedRoute>} />
          <Route path="/reports/serial-tracking" element={<ProtectedRoute><SerialTracking /></ProtectedRoute>} />
          <Route path="/reports/po-tracking" element={<ProtectedRoute><POTracking /></ProtectedRoute>} />
          <Route path="/reports/so-tracking" element={<ProtectedRoute><SOTracking /></ProtectedRoute>} />
          <Route path="/reports/grn-tracking" element={<ProtectedRoute><GRNTracking /></ProtectedRoute>} />
          <Route path="/reports/dc-tracking" element={<ProtectedRoute><DCTracking /></ProtectedRoute>} />
          <Route path="/reports/drafts" element={<ProtectedRoute><DraftReport /></ProtectedRoute>} />

          {/* Report Routes - Purchase */}
          <Route path="/reports/purchase-summary" element={<ProtectedRoute><PurchaseSummary /></ProtectedRoute>} />
          <Route path="/reports/purchase-register" element={<ProtectedRoute><PurchaseRegister /></ProtectedRoute>} />
          <Route path="/reports/party-purchase-summary" element={<ProtectedRoute><PartyPurchaseSummary /></ProtectedRoute>} />
          <Route path="/reports/vendor-payments" element={<ProtectedRoute><VendorPayments /></ProtectedRoute>} />
          <Route path="/reports/purchase-activity" element={<ProtectedRoute><PurchaseActivity /></ProtectedRoute>} />
          <Route path="/reports/item-purchase-analysis" element={<ProtectedRoute><ItemPurchaseAnalysis /></ProtectedRoute>} />
          <Route path="/reports/ap-aging" element={<ProtectedRoute><APAging /></ProtectedRoute>} />
          <Route path="/reports/vendor-balances" element={<ProtectedRoute><VendorBalances /></ProtectedRoute>} />
          <Route path="/reports/landed-cost-analysis" element={<ProtectedRoute><LandedCostAnalysis /></ProtectedRoute>} />
          <Route path="/reports/purchase-cycle" element={<ProtectedRoute><PurchaseCycleReport /></ProtectedRoute>} />

          {/* Report Routes - Sale */}
          <Route path="/reports/sale-summary" element={<ProtectedRoute><SaleSummary /></ProtectedRoute>} />
          <Route path="/reports/sale-cycle" element={<ProtectedRoute><SaleCycleReport /></ProtectedRoute>} />
          <Route path="/reports/sale-register" element={<ProtectedRoute><SaleRegister /></ProtectedRoute>} />
          <Route path="/reports/party-sale-summary" element={<ProtectedRoute><PartySaleSummary /></ProtectedRoute>} />
          <Route path="/reports/collection-report" element={<ProtectedRoute><CollectionReport /></ProtectedRoute>} />
          <Route path="/reports/sale-activity" element={<ProtectedRoute><SaleActivity /></ProtectedRoute>} />
          <Route path="/reports/item-sale-analysis" element={<ProtectedRoute><ItemSaleAnalysis /></ProtectedRoute>} />
          <Route path="/reports/ar-aging" element={<ProtectedRoute><ARAging /></ProtectedRoute>} />
          <Route path="/reports/salesperson-performance" element={<ProtectedRoute><SalespersonPerformance /></ProtectedRoute>} />
          <Route path="/reports/salesman-incentive" element={<ProtectedRoute><SalesmanIncentive /></ProtectedRoute>} />
          <Route path="/reports/pos-sales" element={<ProtectedRoute><POSSalesReport /></ProtectedRoute>} />
          <Route path="/reports/bank-discounts" element={<ProtectedRoute><BankDiscountReport /></ProtectedRoute>} />
          <Route path="/reports/pos-shifts" element={<ProtectedRoute><POSShiftReport /></ProtectedRoute>} />
          <Route path="/reports/pos-branch-cash" element={<ProtectedRoute><POSBranchCashReport /></ProtectedRoute>} />
          <Route path="/reports/customer-balances" element={<ProtectedRoute><CustomerBalances /></ProtectedRoute>} />
          <Route path="/reports/cash-bank-balances" element={<ProtectedRoute><CashBankBalances /></ProtectedRoute>} />
          <Route path="/reports/item-profit-loss" element={<ProtectedRoute><ItemProfitLoss /></ProtectedRoute>} />
          <Route path="/reports/invoice-profit-loss" element={<ProtectedRoute><InvoiceProfitLoss /></ProtectedRoute>} />

          {/* Report Routes - Inventory */}
          <Route path="/reports/inventory-ledger" element={<ProtectedRoute><InventoryLedger /></ProtectedRoute>} />
          <Route path="/reports/inventory-balances" element={<ProtectedRoute><InventoryBalances /></ProtectedRoute>} />
          <Route path="/reports/stock-audit-list" element={<ProtectedRoute><StockAuditList /></ProtectedRoute>} />
          <Route path="/reports/count-difference" element={<ProtectedRoute><CountDifferenceReport /></ProtectedRoute>} />
          <Route path="/reports/stock-loss" element={<ProtectedRoute><StockLossSummary /></ProtectedRoute>} />
          <Route path="/reports/missing-items" element={<ProtectedRoute><MostMissingItems /></ProtectedRoute>} />
          <Route path="/reports/low-stock-alert" element={<ProtectedRoute><LowStockAlert /></ProtectedRoute>} />

          {/* Report Routes - Financial */}
          <Route path="/reports/ledger" element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
          <Route path="/reports/trial-balance" element={<ProtectedRoute><TrialBalance /></ProtectedRoute>} />
          <Route path="/reports/user-activity" element={<ProtectedRoute><UserActivityLog /></ProtectedRoute>} />
          <Route path="/reports/profit-loss" element={<ProtectedRoute><ProfitLoss /></ProtectedRoute>} />
          <Route path="/reports/balance-sheet" element={<ProtectedRoute><BalanceSheet /></ProtectedRoute>} />
          <Route path="/reports/cash-flow" element={<ProtectedRoute><CashFlow /></ProtectedRoute>} />
          <Route path="/reports/tax-reports" element={<ProtectedRoute><TaxReports /></ProtectedRoute>} />

          {/* Report Routes - Dashboard Intelligence */}
          <Route path="/reports/financial-health" element={<ProtectedRoute><FinancialHealthReport /></ProtectedRoute>} />
          <Route path="/reports/sales-intelligence" element={<ProtectedRoute><SalesIntelligenceReport /></ProtectedRoute>} />
          <Route path="/reports/inventory-intelligence" element={<ProtectedRoute><InventoryIntelligenceReport /></ProtectedRoute>} />
          <Route path="/reports/stock-transfer" element={<ProtectedRoute><StockTransferReport /></ProtectedRoute>} />
          <Route path="/reports/branch-transfer" element={<ProtectedRoute><BranchTransferReport /></ProtectedRoute>} />
          <Route path="/reports/transfer-report" element={<ProtectedRoute><TransferReport /></ProtectedRoute>} />
          <Route path="/reports/cash-flow-management" element={<ProtectedRoute><CashFlowManagementReport /></ProtectedRoute>} />
          <Route path="/reports/operational-metrics" element={<ProtectedRoute><OperationalMetricsReport /></ProtectedRoute>} />

          {/* Report Routes - Jobs */}
          <Route path="/reports/job-cost-sheet" element={<ProtectedRoute><JobCostSheet /></ProtectedRoute>} />
          <Route path="/reports/job-profit-loss" element={<ProtectedRoute><JobProfitLoss /></ProtectedRoute>} />

          {/* Report Routes - Production */}
          <Route path="/reports/production-summary" element={<ProtectedRoute><ProductionSummary /></ProtectedRoute>} />
          <Route path="/reports/production-variance" element={<ProtectedRoute><ProductionVariance /></ProtectedRoute>} />
          <Route path="/reports/material-issue" element={<ProtectedRoute><MaterialIssueReport /></ProtectedRoute>} />
          <Route path="/reports/production-receipt" element={<ProtectedRoute><ProductionReceiptReport /></ProtectedRoute>} />

          {/* Help */}
          <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        </Routes>
        {/* Onboarding wizard — overlays on top of any route; shows only for a
            new company's owner until completed or skipped. */}
        <OnboardingWizard />
      </AuthProvider>
    </Router>
  );
}

export default App;
